import { Controller } from "@hotwired/stimulus"

// Mehrfach-Datei-Anhänge per Drag & Drop für die E-Mail-Versand-Modals
// (Profil versenden + Angebot versenden). Sammelt Dateien in this.files,
// validiert Typ (PDF/ZIP) + Größe clientseitig, rendert entfernbare Chips
// und schreibt nur gültige Dateien in den versteckten multiple-File-Input.
export default class extends Controller {
  static targets = ["input", "list", "zone"]
  static values = {
    accept: { type: String, default: ".pdf,.zip" },
    maxMb: { type: Number, default: 10 },
    errorType: { type: String, default: "Nur PDF & ZIP erlaubt" },
    errorSize: { type: String, default: "Datei zu groß" }
  }

  connect() {
    this.files = []
    this.allowed = this.acceptValue
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
  }

  dragover(event) {
    event.preventDefault()
    this.zoneTarget.classList.add("border-blue-500", "bg-blue-50")
  }

  dragleave(event) {
    event.preventDefault()
    this.zoneTarget.classList.remove("border-blue-500", "bg-blue-50")
  }

  drop(event) {
    event.preventDefault()
    this.zoneTarget.classList.remove("border-blue-500", "bg-blue-50")
    this.addFiles(event.dataTransfer.files)
  }

  picked(event) {
    this.addFiles(event.target.files)
  }

  addFiles(fileList) {
    Array.from(fileList).forEach((file) => {
      const ext = "." + (file.name.split(".").pop() || "").toLowerCase()
      const duplicate = this.files.some((f) => f.name === file.name && f.size === file.size)
      if (duplicate) return
      this.files.push({
        name: file.name,
        size: file.size,
        file: file,
        okType: this.allowed.includes(ext),
        okSize: file.size <= this.maxMbValue * 1024 * 1024
      })
    })
    this.sync()
    this.render()
  }

  remove(event) {
    const index = parseInt(event.params.index, 10)
    this.files.splice(index, 1)
    this.sync()
    this.render()
  }

  // Nur gültige Dateien landen im echten Upload-Input.
  sync() {
    const dt = new DataTransfer()
    this.files
      .filter((f) => f.okType && f.okSize)
      .forEach((f) => dt.items.add(f.file))
    this.inputTarget.files = dt.files
  }

  render() {
    this.listTarget.innerHTML = ""
    this.files.forEach((f, index) => {
      const invalid = !f.okType || !f.okSize
      const li = document.createElement("li")
      li.className =
        "flex items-center justify-between gap-2 py-2 pl-3 pr-2 rounded-md border text-sm " +
        (invalid ? "border-red-300 bg-red-50" : "border-gray-200 bg-white")

      const left = document.createElement("div")
      left.className = "flex items-center gap-2 min-w-0"
      left.innerHTML =
        '<i class="fas fa-file-pdf flex-shrink-0 ' +
        (invalid ? "text-red-400" : "text-gray-400") +
        '"></i>'

      const text = document.createElement("div")
      text.className = "flex flex-col min-w-0"
      const name = document.createElement("span")
      name.className = "truncate " + (invalid ? "text-red-700" : "text-gray-900")
      name.textContent = f.name
      text.appendChild(name)

      const meta = document.createElement("span")
      meta.className = "text-xs " + (invalid ? "text-red-600" : "text-gray-500")
      if (!f.okType) {
        meta.textContent = this.errorTypeValue
      } else if (!f.okSize) {
        meta.textContent = this.errorSizeValue + " (max. " + this.maxMbValue + " MB)"
      } else {
        meta.textContent = this.humanSize(f.size)
      }
      text.appendChild(meta)
      left.appendChild(text)

      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "flex-shrink-0 text-gray-400 hover:text-red-600 px-2"
      btn.setAttribute("aria-label", "Datei entfernen")
      btn.dataset.action = "email-attachments#remove"
      btn.dataset.emailAttachmentsIndexParam = index
      btn.innerHTML = '<i class="fas fa-times"></i>'

      li.appendChild(left)
      li.appendChild(btn)
      this.listTarget.appendChild(li)
    })
  }

  humanSize(bytes) {
    if (bytes < 1024) return bytes + " B"
    if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + " KB"
    return (bytes / (1024 * 1024)).toFixed(1) + " MB"
  }
}
